/**
 * Local-first hooks for the CLASS PLAN / flipped classroom (M5).
 *
 * session_module arrives via the cohort streams (my_cohorts_student/teacher):
 * the student sees their plan OFFLINE. Completion ticks are composed by
 * reusing progress that already exists (lesson_progress,
 * assignment_submission, video_watch_progress). Writes via write-path
 * (@space/shared mutations).
 */
import type { Accessor } from "solid-js";
import {
  type CourseModuleDirectoryRow,
  getAllWatchProgressQuery,
  getClassSessionRowQuery,
  getCourseModuleDirectoryQuery,
  getCourseProgressQuery,
  getMySubmissionsQuery,
  getSessionModulesByCohortQuery,
  getSessionModulesBySessionQuery,
  sessionPlanQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import type { ClassSession, SessionModule } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Plan items (session_module) for a session. */
export function useSessionPlanItems(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<SessionModule>({
    queryKey: () => sessionPlanQueryKeys.bySession(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getSessionModulesBySessionQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Plan items for the WHOLE cohort (to mark "has a plan" in the schedule). */
export function useCohortPlanItems(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<SessionModule>({
    queryKey: () => sessionPlanQueryKeys.byCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getSessionModulesByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** A session's class_session row (the plan's header). */
export function useSessionRow(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => sessionPlanQueryKeys.sessionRow(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getClassSessionRowQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Course module directory with resolved title (for rendering and picking). */
export function useCourseModuleDirectory(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CourseModuleDirectoryRow>({
    queryKey: () => sessionPlanQueryKeys.directory(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseModuleDirectoryQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

interface LessonProgressRow {
  lesson_id: string | null;
  status: string | null;
  /** Lesson's 0..1 score (`auto_blocks` source for the student's report card). */
  score: number | null;
}
interface SubmissionRow {
  assignment_id: string | null;
}
interface WatchRow {
  video_id: string | null;
  completed_at: string | null;
}

/** MY lesson progress in a course (for the plan's ticks). */
export function useMyCourseProgress(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<LessonProgressRow>({
    queryKey: () => sessionPlanQueryKeys.myProgress(base.userId(), courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      const id = courseId();
      return db && uid && id ? getCourseProgressQuery(db, uid, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY submissions (for the tick on the plan's assignment modules). */
export function useMySubmissions() {
  const base = useBase();
  return createPowerSyncQuery<SubmissionRow>({
    queryKey: () => sessionPlanQueryKeys.mySubmissions(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMySubmissionsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY video watch progress (for the tick on video modules). */
export function useMyWatchProgress() {
  const base = useBase();
  return createPowerSyncQuery<WatchRow>({
    queryKey: () => sessionPlanQueryKeys.myWatch(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getAllWatchProgressQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
